import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Ship, Users, UserRoundPlus, Anchor, Waves, Sun, Moon, Ticket, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Button from '../components/ui/button';
import Footer from '../components/Footer';

const Home = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [clientes, setClientes] = useState([]);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [embarcacionesEnAgua, setEmbarcacionesEnAgua] = useState([]);
  const [embarcacionesFueraDelAgua, setEmbarcacionesFueraDelAgua] = useState([]);
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const activities = [
    { description: 'Nueva reserva: Juan Pérez - Velero "Estrella del Mar"', timeAgo: 'Hace 5 minutos' },
    { description: 'Factura pagada: María González - Yate "Ola Azul"', timeAgo: 'Hace 1 hora' },
    { description: 'Salida registrada: Carlos Rodríguez - Lancha "Rayo Veloz"', timeAgo: 'Hace 2 horas' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Obtener movimientos
        const movimientosResponse = await api.get('/movimientos');
        
        // Procesar respuestas
        const movimientos = Array.isArray(movimientosResponse.data) 
          ? movimientosResponse.data 
          : [];

        setEmbarcacionesEnAgua(movimientos.filter(mov => mov.estado === 'En el agua'));
        setEmbarcacionesFueraDelAgua(movimientos.filter(mov => mov.estado === 'Fuera del agua'));
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Error al cargar los datos. Por favor intente nuevamente.');
        setEmbarcacionesEnAgua([]);
        setEmbarcacionesFueraDelAgua([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Función para buscar todos los clientes sin límite
  const searchAllClientes = async (term) => {
    if (!term.trim()) {
      setFilteredClientes([]);
      return;
    }
  
    setSearchLoading(true);
    try {
      const response = await api.get('/clientes', {
        params: {
          search: term,
          limit: 100 // Aumentar el límite para mejores resultados
        }
      });
      
      const processedClientes = (response.data.clientes || []).map(cliente => ({
        ...cliente,
        embarcaciones: cliente.embarcaciones || []
      }));
      
      setFilteredClientes(processedClientes);
    } catch (error) {
      console.error('Error al buscar clientes:', error);
      setFilteredClientes([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounce para la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      searchAllClientes(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const quickAccessItems = [
    { icon: <Ship className="h-6 w-6" />, title: "Movimientos", link: "/movimientos" },
    { icon: <Ticket className="h-6 w-6" />, title: "Ingresos", link: "/registros" },
    { icon: <Users className="h-6 w-6" />, title: "Clientes", link: "/clientes" },
    { icon: <Anchor className="h-6 w-6" />, title: "Guardería", link: "/guarderia" },
    { icon: <UserRoundPlus className="h-6 w-6" />, title: "Nuevo Cliente", link: "/nuevocliente" },
  ];

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark', !darkMode);
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-blanco-puro'}`}>
        <div className="text-2xl font-semibold">Cargando...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-blanco-puro'}`}>
        <div className="text-2xl font-semibold text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-blanco-puro text-azul-oscuro'}`}>
      <Navbar />
      <main className="container mx-auto mt-8 px-4">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-center">Bienvenido a DRIZA</h1>
          <Button 
            onClick={toggleDarkMode} 
            className="bg-verde-esmeralda text-white hover:bg-amarillo-dorado"
            aria-label={darkMode ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
          >
            {darkMode ? <Sun className="h-6 w-6" /> : <Moon className="h-6 w-6" />}
          </Button>
        </div>
        
        {/* Barra de búsqueda mejorada */}
        <div className="relative max-w-2xl mx-auto mb-8">
          <div className={`relative flex items-center border ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gris-piedra bg-white'} rounded-md shadow-sm`}>
            <Search className="absolute left-3 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar clientes por nombre, apellido, ID..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className={`w-full pl-10 pr-4 py-3 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white'} rounded-md focus:outline-none focus:ring-2 focus:ring-verde-esmeralda`}
            />
            {searchLoading && (
              <div className="absolute right-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-verde-esmeralda"></div>
              </div>
            )}
          </div>
          
          {/* Resultados en dropdown */}
          {/* {searchTerm && (
            <div className={`absolute z-10 mt-1 w-full ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg rounded-md border ${darkMode ? 'border-gray-700' : 'border-gray-200'} max-h-96 overflow-auto`}>
              {searchLoading ? (
                <div className="p-4 text-center">Buscando...</div>
              ) : filteredClientes.length > 0 ? (
                <ul>
                  {filteredClientes.map(cliente => (
                    <Link 
                      to={`/detallesclientes/${cliente.id}`} 
                      key={cliente.id}
                    >
                      <li className={`p-3 hover:${darkMode ? 'bg-gray-700' : 'bg-gray-50'} cursor-pointer border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                        <div className="font-medium">
                          {cliente.nombre} {cliente.apellido}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {cliente.id} {cliente.dni && `| DNI: ${cliente.dni}`}
                        </div>
                      </li>
                    </Link>
                  ))}
                </ul>
              ) : (
                <div className="p-4 text-gray-500">No se encontraron clientes</div>
              )}
            </div>
          )}*/}
        </div> 
        
        {/* Resultados de búsqueda en tarjetas (se mantiene igual) */}
        {searchTerm && (
          <div className="mt-8">
            <h2 className="text-2xl font-semibold mb-4">
              {searchLoading 
                ? 'Buscando...' 
                : filteredClientes.length > 0 
                  ? `Resultados de búsqueda (${filteredClientes.length})` 
                  : `No se encontró el cliente: "${searchTerm}"`}
            </h2>
            
            {!searchLoading && filteredClientes.length > 0 && (
              <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {filteredClientes.map(cliente => (
                  <Link 
                    to={`/detallesclientes/${cliente.id}`} 
                    key={cliente.id}
                    className="hover:shadow-lg transition-transform"
                  >
                    <Card className={`h-full flex flex-col ${darkMode ? 'bg-gray-800' : ''}`}>
                      <CardHeader>
                        <CardTitle className="text-lg font-semibold">
                          {cliente.nombre} {cliente.apellido}
                        </CardTitle>
                        <CardDescription>ID {cliente.id}</CardDescription>
                      </CardHeader>
                      <CardContent className="flex-grow">
                        <p>Teléfono: {cliente.telefono1}</p>
                        {cliente.observaciones && (
                          <p className="truncate">Observaciones: {cliente.observaciones}</p>
                        )}
                        {cliente.embarcaciones?.length > 0 && (
                          <p className="mt-2 text-sm">Embarcaciones: {cliente.embarcaciones.length}</p>
                        )}
                      </CardContent>
                      <CardFooter>
                        <Button className="bg-verde-esmeralda text-white hover:bg-amarillo-dorado w-full">
                          Ver detalles
                        </Button>
                      </CardFooter>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Acceso rápido */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">Acceso Rápido</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {quickAccessItems.map((item, index) => (
              <Link to={item.link} key={index} className="group">
                <Card className={`h-full flex items-center justify-center p-4 transition-all ${darkMode ? 'bg-gray-800 group-hover:bg-gray-700' : 'bg-azul-oscuro text-white group-hover:bg-verde-esmeralda'}`}>
                  <CardContent className="flex flex-col items-center p-0">
                    <div className={`p-3 rounded-full mb-2 ${darkMode ? 'bg-gray-700' : 'bg-white text-azul-oscuro'}`}>
                      {item.icon}
                    </div>
                    <span className="text-center font-medium">{item.title}</span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>
        
        {/* Estado de embarcaciones */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">Estado de Embarcaciones</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <Card className={`text-center p-6 ${darkMode ? 'bg-gray-800' : 'bg-azul-oscuro text-white'}`}>
              <Anchor className="h-12 w-12 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold mb-2">{embarcacionesEnAgua.length}</h3>
              <p className="text-lg">En el Agua</p>
            </Card>
            <Card className={`text-center p-6 ${darkMode ? 'bg-gray-800' : 'bg-azul-oscuro text-white'}`}>
              <Waves className="h-12 w-12 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold mb-2">{embarcacionesFueraDelAgua.length}</h3>
              <p className="text-lg">Fuera del Agua</p>
            </Card>
          </div>
          <div className="flex justify-center mt-6">
            <Link to="/movimientos">
              <Button className="bg-verde-esmeralda text-white hover:bg-amarillo-dorado px-6 py-3">
                Ver Tabla de Movimientos
              </Button>
            </Link>
          </div>
        </section>

        {/* Actividades recientes */}
        <section className="mb-12 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-center">Actividades Recientes</h2>
          <Card className={`p-6 ${darkMode ? 'bg-gray-800' : ''}`}>
            <ul className="space-y-4">
              {activities.map((activity, index) => (
                <li key={index} className="border-b pb-4 last:border-0 last:pb-0 border-gray-200 dark:border-gray-700">
                  <p className="font-medium">{activity.description}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{activity.timeAgo}</p>
                </li>
              ))}
            </ul>
          </Card>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Home;